"use client";

import { ChevronDown, MoreHorizontal } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

/**
 * 「更多」溢出菜单 —— 页头次要操作的收口位。
 *
 * 为什么需要（2026-10-07 分镜页 UI 整理）：
 *   页头原本平铺 6 个按钮（提示词 / 视图 / 对比 / 预览 / 匹配 / 导出）。实测 1280 宽就换行，
 *   换行后各按钮左边界参差；而其中「版本对比」「导出全部」「提示词编辑」是**低频**操作，
 *   却和「预览」「一键匹配」这种每轮都要点的按钮抢同一层视觉权重。
 *
 * ## 🔴 为什么必须用 portal（2026-10-08 用户反馈「选项被遮挡了，无法操作」）
 *
 * 原实现是浮层 `absolute right-0 top-full`，结果在项目卡上**根本点不到**：
 *   项目卡的结构是「封面区（`overflow-hidden`）+ 正文区」，更多按钮位于封面右下角，
 *   `top-full` 让菜单向下展开 —— 而下方 165px 之内全是封面区，被 `overflow-hidden` 裁死。
 *   实测（scripts/_card_menu_probe.mjs，1920 视口）：菜单矩形 y=420~462，
 *   封面区底边 y=424 —— **42px 高的菜单只有 4px 露在外面**。
 *   命中测试（elementFromPoint 取菜单视觉中心）返回的是卡片标题 `<span class=line-clamp-2>`，
 *   不是菜单里的「删除」按钮 —— 即菜单项**完全不可见也不可点**，用户看到的是一条 4px 白边。
 *
 * 🔴 这个 bug 的隐蔽之处：`z-50` 写得再大也没用。**overflow:hidden 的裁剪发生在
 *    z-index 之前**（先决定"哪些后代可见"，再比层叠顺序），所以「加 z 索引」这一类
 *    直觉修法在这里完全无效。唯一的根治是**不把浮层放在裁剪容器里**。
 *
 * 方案选择（三选一）：
 *   ① 改用 portal 挂到 body + fixed 定位（采用）：脱离一切 overflow 裁剪；
 *      顺带拿到视口边缘自动翻转（见下方 flip 逻辑），网格里最后一张卡点开也不会出屏。
 *   ② 给所有祖先加 `overflow:visible`：会破坏封面圆角裁剪与卡片阴影，卡片视觉直接崩。
 *   ③ 改用 CSS Popover API：需要 `popover="manual"` + 逐个调用 `showPopover()`，
 *      且仍受 top-layer 之外的祖先 clip 影响，改动面反而更大。
 *
 * 位置改fixed 之后必须自己算坐标（原`absolute` 由 CSS 负责）：
 *   · 默认贴在触发器**下方**右对齐（与原视觉一致）
 *   · 下方空间不足时**翻到上方**；上方也不够（视口极矮）才改为下方并压缩高度
 *   · 水平方向做边界收敛，避免贴边卡被推出视口
 *   · 滚动/缩放时重新定位 —— fixed 坐标不再跟随，浮层会与按钮错位
 */
export interface MoreMenuItem {
  key: string;
  /** 文本条目；给 `node` 时本字段忽略 */
  label?: string;
  icon?: ReactNode;
  /** 自定义内容（本身就是可交互元素时用它） */
  node?: ReactNode;
  onSelect?: () => void;
  /** 高亮为"当前已开启"（如版本对比已打开） */
  active?: boolean;
  disabled?: boolean;
  /** 悬停说明（菜单项文案必须短，解释放这里） */
  title?: string;
}

/** 浮层与触发器的间距 */
const GAP = 4;
/** 浮层最小宽度，与 `min-w-[180px]` 保持一致 */
const MIN_W = 180;
/** 视口边缘最小留白 */
const VIEWPORT_PAD = 8;

interface Pos {
  top: number;
  left: number;
}

export function MoreMenu({
  items,
  label = "更多操作",
  placement = "auto",
  triggerLabel,
  badge,
}: {
  items: MoreMenuItem[];
  label?: string;
  /**
   * 展开方向。
   * - `"auto"`（默认）：先下方，溢出时翻上方 —— 页头这类触发器下方是正文的情况
   * - `"top"`：优先上方 —— 触发器本身就在容器底部（项目卡封面右下角）时用，
   *   向下展开虽然可点，但会压住卡片标题与指标格这些识别信息
   */
  placement?: "auto" | "top";
  /**
   * 文字触发器（2026-10-27 第十一次改版：分镜页「整体优化」）。
   *
   * 不传 = 老行为（32×32 的 ⋯ 图标按钮，页头次要操作的收口位）。
   * 传了 = 带文字的胶囊下拉（如「版本 20260918-V4 ▾」「批量修整 ▾ 2」）——
   * 收纳位从"一个图标"升级成"一个说得出自己是什么的菜单"。
   *
   * 为什么不在 MoreMenu 里另起一个组件：浮层定位、portal、Escape/点外部关闭、
   * 滚动重算这套逻辑已经有 100 行且踩过坑（见文件头），复制第二份必然分叉。
   */
  triggerLabel?: string;
  /** 触发器上的待处理计数（如"有 2 项要修"）。有计数时按钮转琥珀色，无则不显示。 */
  badge?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<Pos | null>(null);
  // SSR 期间没有 document，portal 必须等服务端渲染结束后再挂
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  /** 依据触发器位置与浮层实际尺寸算定位 */
  const place = useCallback(() => {
    const t = triggerRef.current;
    const p = panelRef.current;
    if (!t || !p) return;
    const tb = t.getBoundingClientRect();
    const pb = p.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    // ① 垂直方向
    //    · placement="top"：优先上方（触发器在封面底部时用 —— 向下展开会压住
    //      卡片标题与指标格，虽然可点但挡住识别信息）
    //    · 默认 "auto"：优先下方；下方放不下且上方更宽裕才翻到上方
    const below = tb.bottom + GAP;
    const above = tb.top - GAP - pb.height;
    let top = below;
    if (placement === "top") {
      top = above >= VIEWPORT_PAD ? above : below;
    } else if (below + pb.height > vh - VIEWPORT_PAD && above >= VIEWPORT_PAD) {
      top = above;
    }
    // 上下都不理想时收敛进视口，绝不出屏
    top = Math.max(VIEWPORT_PAD, Math.min(top, vh - pb.height - VIEWPORT_PAD));

    // ② 水平：默认右对齐触发器（与原 `right-0` 一致），越界则左移，极窄时收敛到视口内
    let left = tb.right - pb.width;
    if (left + pb.width > vw - VIEWPORT_PAD) left = vw - VIEWPORT_PAD - pb.width;
    if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;

    setPos({ top, left });
  }, [placement]);

  // 浮层首帧拿到真实宽高后才能定位，所以放在 layout 阶段（避免先闪一下左上角）
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    place();
  }, [open, place]);

  // 定位依赖视口尺寸与滚动位置：fixed 坐标不跟随锚点，页面一动就必须重算
  useEffect(() => {
    if (!open) return;
    const reposition = () => place();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const inRoot = rootRef.current?.contains(e.target as Node);
      const inPanel = panelRef.current?.contains(e.target as Node);
      if (!inRoot && !inPanel) setOpen(false);
    }
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const visible = items.filter(Boolean);
  if (visible.length === 0) return null;

  const panel = open ? (
    <div
      ref={panelRef}
      role="menu"
      style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
      className="fixed z-[1000] flex min-w-[180px] flex-col gap-0.5 rounded-xl border border-[var(--border-subtle)] bg-white p-1.5 shadow-lg shadow-black/5"
    >
      {visible.map((item) => {
        if (item.node) {
          return (
            <div key={item.key} role="none" className="px-0.5 py-0.5">
              {item.node}
            </div>
          );
        }
        return (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            disabled={item.disabled}
            title={item.title}
            onClick={() => {
              item.onSelect?.();
              setOpen(false);
            }}
            className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
              item.active
                ? "bg-primary/8 font-medium text-primary"
                : "text-[var(--text-secondary)] hover:bg-[var(--surface)] hover:text-[var(--text-primary)]"
            }`}
          >
            {item.icon ? <span className="shrink-0 text-[var(--text-muted)]">{item.icon}</span> : null}
            <span className="truncate">{item.label}</span>
          </button>
        );
      })}
    </div>
  ) : null;

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => setOpen((o) => !o)}
        data-more-menu-trigger={triggerLabel ?? ""}
        className={
          triggerLabel
            ? /* 触发器恒为中性描边（与「版本 ▾」是同级菜单，长得一样才好认）。
               有待处理项时只把**计数**点成琥珀（小圆点 + 琥珀数字），不再整颗填琥珀底 ——
               实测填底色时它和右边的「一键续跑」（琥珀渐变主操作）并排两个琥珀面，
               次要动作看着跟主操作一样重（老板 2026-10-27：「还需要优化 更加美观 合理」）。 */
              "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-lg border border-[var(--border-hover)] bg-white pl-2 pr-1.5 text-[12px] font-medium text-[var(--text-secondary)] transition-colors hover:border-primary/40 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
            : "flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--border-subtle)] bg-white text-[var(--text-secondary)] transition-colors hover:border-primary/40 hover:bg-primary/3 hover:text-primary"
        }
      >
        {triggerLabel ? (
          <>
            <span className="whitespace-nowrap">{triggerLabel}</span>
            {badge ? (
              <span className="flex shrink-0 items-center gap-1 text-amber-600">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                {badge}
              </span>
            ) : null}
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
          </>
        ) : (
          <MoreHorizontal className="h-4 w-4" />
        )}
      </button>

      {/* portal 到 body：脱离所有祖先的 overflow 裁剪（详见文件头注释） */}
      {mounted && panel ? createPortal(panel, document.body) : null}
    </div>
  );
}